import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import {
  FaEye, FaTrashAlt, FaSearch, FaTimes, FaUser,
  FaShippingFast, FaCreditCard, FaBoxOpen, FaCalendarAlt,
  FaTruck, FaBan, FaTimesCircle
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { fetchUnverifiedOrders, fetchVerifiedOrders } from '../api/api';

const CancelledOrderList: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAgent, setIsAgent] = useState<boolean>(false);

  const [searchIdOrName, setSearchIdOrName] = useState<string>('');
  const [filterPayment, setFilterPayment] = useState<string>('All');
  const [filterDate, setFilterDate] = useState<string>('');

  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    const checkUserRole = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(
            decodeURIComponent(
              window
                .atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            )
          );

          if (JSON.stringify(payload).toUpperCase().includes('AGENT')) {
            setIsAgent(true);
          }
        }
      } catch (e) {
        console.error('Error decoding token', e);
      }
    };

    checkUserRole();
  }, []);

  // ✅ Delhivery/Ekart/Shiprocket sabka AWB alag-alag field me aa sakta hai.
  const getWaybill = (o: any) => {
    return (
      o.delhiveryWaybill ||
      o.waybill ||
      o.awb ||
      o.awbNumber ||
      o.trackingId ||
      o.trackingNumber ||
      o.shipmentId ||
      o.shiprocketAwb ||
      o.ekartTrackingId ||
      null
    );
  };

  const normalizePartner = (partner?: string | null) => {
    const p = String(partner || 'DELHIVERY').trim().toUpperCase();
    if (p.includes('EKART')) return 'EKART';
    if (p.includes('SHIPROCKET')) return 'SHIPROCKET';
    if (p.includes('DELHIVERY')) return 'DELHIVERY';
    return p || 'DELHIVERY';
  };

  const getPartnerFromOrder = (o: any) => {
    return normalizePartner(
      o.deliveryPartner ||
      o.delivery_partner ||
      o.partner ||
      o.courierPartner ||
      o.courier ||
      o.shippingPartner ||
      o.logisticsPartner
    );
  };

  // ✅ Live tracking response me status ka format har partner ka alag ho sakta hai.
  //    Isliye sabko ek common format me convert kar rahe hain.
  const normalizeStatus = (status?: string | null) => {
    let s = String(status || '').trim().toUpperCase();

    if (!s || s === 'UNKNOWN' || s === 'NULL' || s === 'TRACKING INFO FETCHED') return '';

    s = s.replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();

    // Cancel page ke liye sabse important condition.
    if (
      s.includes('CANCEL') ||
      s.includes('CANCELED') ||
      s.includes('CANCELLATION') ||
      s.includes('SHIPMENT CANCELLED') ||
      s.includes('ORDER CANCELLED')
    ) {
      return 'CANCELLED';
    }

    if (s.includes('DELIVERED') || s.includes('DELIVERY DONE') || s.includes('SHIPMENT DELIVERED')) return 'DELIVERED';
    if (s.includes('OUT FOR DELIVERY') || s === 'OFD') return 'OUT_FOR_DELIVERY';
    if (s.includes('RTO') && s.includes('TRANSIT')) return 'RTO_IN_TRANSIT';
    if (s.includes('RTO')) return 'RTO';
    if (s.includes('RETURN')) return 'RETURNED';
    if (s.includes('IN TRANSIT')) return 'IN_TRANSIT';
    if (s.includes('DISPATCHED') || s.includes('SHIPPED')) return 'SHIPPED';
    if (s.includes('MANIFEST')) return 'MANIFESTED';
    if (s.includes('PENDING') || s.includes('ORDER PLACED')) return 'PENDING';

    return s.replace(/\s/g, '_');
  };

  const safeParseJson = (value: any) => {
    if (!value) return null;
    if (typeof value === 'object') return value;

    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  };

  const extractLiveStatus = (data: any, partner: string) => {
    // Backend agar clean status de raha hai, sabse pehle wahi uthao.
    const cleanStatus = normalizeStatus(
      data?.currentStatus ||
      data?.status ||
      data?.shipmentStatus ||
      data?.trackingStatus ||
      data?.current_status ||
      data?.shipment_status ||
      data?.courier_status
    );

    if (cleanStatus) return cleanStatus;

    // Delhivery / Shiprocket / Ekart me kabhi rawResponse ke andar status hota hai.
    const raw = safeParseJson(data?.rawResponse);
    if (raw) {
      const delhiveryStatus = normalizeStatus(raw?.ShipmentData?.[0]?.Shipment?.Status?.Status);
      if (delhiveryStatus) return delhiveryStatus;

      const shiprocketStatus = normalizeStatus(
        raw?.tracking_data?.shipment_track?.[0]?.current_status ||
        raw?.tracking_data?.shipment_track?.[0]?.delivered_to ||
        raw?.tracking_data?.shipment_status ||
        raw?.tracking_data?.track_status ||
        raw?.current_status ||
        raw?.status
      );
      if (shiprocketStatus) return shiprocketStatus;

      const ekartStatus = normalizeStatus(
        raw?.currentStatus ||
        raw?.current_status ||
        raw?.shipment_status ||
        raw?.tracking_status ||
        raw?.status
      );
      if (ekartStatus) return ekartStatus;
    }

    // Partner param currently direct logic ke liye required nahi hai,
    // but future partner-specific parsing ke liye yahan rakha hai.
    console.debug('Tracking partner:', partner);
    return '';
  };

  const fetchOrders = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      const [unverifiedRes, verifiedRes] = await Promise.all([
        fetchUnverifiedOrders().catch(() => ({ data: [] })),
        fetchVerifiedOrders().catch(() => ({ data: [] }))
      ]);

      const crmOrders = [...(unverifiedRes.data || []), ...(verifiedRes.data || [])].map((o: any) => {
        const waybill = getWaybill(o);
        const partner = getPartnerFromOrder(o);
        const dbStatus = normalizeStatus(o.status);

        return {
          id: o.id,
          user: {
            firstName: o.mainUser?.firstName || o.customerName || 'CRM Customer',
            lastName: o.mainUser?.lastName || '',
            email: o.mainUser?.email || o.email || 'N/A',
            mobile: o.mainUser?.mobile || o.mobile || 'N/A'
          },
          orderDate: o.cancelledAt || o.canceledAt || o.updatedAt || o.createdAt || new Date().toISOString(),
          delhiveryWaybill: waybill,
          deliveryPartner: partner,
          totalAmount: o.payableAmount || 0,
          // ✅ Pehle yahan hasWaybill hone par direct MANIFESTED ho raha tha,
          //    isliye DB me CANCELLED order bhi hide ho sakta tha. Ab pehle DB status rakhenge.
          status: dbStatus || (waybill ? 'MANIFESTED' : 'UNVERIFIED'),
          paymentMode: 'COD',
          paymentStatus: dbStatus === 'DELIVERED' ? 'PAID' : 'PENDING',
          source: 'CRM',
          originalData: o
        };
      });

      let unifiedOrders: any[] = [];

      if (isAgent) {
        unifiedOrders = crmOrders;
      } else {
        // const webRes = await axios.get('http://localhost:8484/api/orders/all', {
          const webRes = await axios.get('https://www.ayushvatika.com/api/orders/all', {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }));

        const webOrders = (webRes.data || []).map((o: any) => {
          const waybill = getWaybill(o);
          const partner = getPartnerFromOrder(o);
          const dbStatus = normalizeStatus(o.status);

          return {
            ...o,
            orderDate: o.cancelledAt || o.canceledAt || o.updatedAt || o.orderDate || o.createdAt || new Date().toISOString(),
            delhiveryWaybill: waybill,
            deliveryPartner: partner,
            status: dbStatus || (waybill ? 'MANIFESTED' : 'PLACED'),
            paymentStatus: dbStatus === 'DELIVERED' ? 'PAID' : (o.paymentStatus || 'PENDING'),
            source: 'WEBSITE',
            originalData: o
          };
        });

        unifiedOrders = [...webOrders, ...crmOrders];
      }

      unifiedOrders.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      setOrders(unifiedOrders);

      // ✅ DB me cancel status save nahi hai to live tracking se CANCELLED identify hoga.
      // ✅ Important: ?partner=${partner} bhejna zaroori hai, warna Ekart/Shiprocket galat route pe chale jaate hain.
      const trackableOrders = unifiedOrders.filter(o => {
        const status = normalizeStatus(o.status);
        return (
          o.delhiveryWaybill &&
          status !== 'DELIVERED' &&
          status !== 'RTO' &&
          status !== 'RTO_IN_TRANSIT' &&
          status !== 'RETURNED'
        );
      });

      if (trackableOrders.length > 0) {
        (async () => {
          for (const order of trackableOrders) {
            try {
              const partner = normalizePartner(order.deliveryPartner);

              const res = await axios.post(
                // `http://localhost:8484/api/delivery/track?partner=${partner}`,
                `https://www.ayushvatika.com/api/delivery/track?partner=${partner}`,
                { waybill: order.delhiveryWaybill },
                { headers: { Authorization: `Bearer ${token}` } }
              );

              const liveStatus = extractLiveStatus(res.data, partner);

              if (liveStatus) {
                setOrders(prev => prev.map(o =>
                  o.id === order.id
                    ? {
                        ...o,
                        status: liveStatus,
                        paymentStatus: liveStatus === 'DELIVERED' ? 'PAID' : o.paymentStatus
                      }
                    : o
                ));
              }
            } catch (e) {
              console.error(`Tracking failed for ${order.deliveryPartner} AWB: ${order.delhiveryWaybill}`, e);
            }
          }
        })();
      }
    } catch (err: any) {
      toast.error('Orders load karne mein error aayi!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgent]);

  // ✅ Sirf cancelled orders dikhenge, chahe status DB se aaye ya live tracking se.
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const currentStatus = normalizeStatus(order.status);
      if (currentStatus !== 'CANCELLED') return false;

      const customerFullName = `${order.user.firstName} ${order.user.lastName}`.toLowerCase();
      const searchValue = searchIdOrName.toLowerCase();

      const matchesSearch =
        String(order.id).toLowerCase().includes(searchValue) ||
        customerFullName.includes(searchValue) ||
        (order.delhiveryWaybill && String(order.delhiveryWaybill).toLowerCase().includes(searchValue));

      const matchesPayment = filterPayment === 'All' || order.paymentMode === filterPayment || order.paymentStatus === filterPayment;
      const orderDateOnly = new Date(order.orderDate).toISOString().split('T')[0];
      const matchesDate = !filterDate || orderDateOnly === filterDate;

      return matchesSearch && matchesPayment && matchesDate;
    });
  }, [orders, searchIdOrName, filterPayment, filterDate]);

  const handleDelete = async (id: string, source: string) => {
    if (!window.confirm('Kya aap is cancelled order ko delete karna chahte hain?')) return;

    try {
      const token = localStorage.getItem('token');

      if (source === 'CRM') {
        // await axios.delete(`http://localhost:8484/api/admin/delete-order/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        await axios.delete(`https://www.ayushvatika.com/api/admin/delete-order/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // await axios.delete(`http://localhost:8484/api/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        await axios.delete(`https://www.ayushvatika.com/api/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Order deleted!');
      fetchOrders();
    } catch (err: any) {
      toast.error('Delete failed!');
    }
  };

  const handleTrack = (waybill: string, partner: string) => {
    if (!waybill) return;

    const p = normalizePartner(partner);

    if (p === 'EKART') {
      window.open(`https://ekartlogistics.com/shipmenttrack/${waybill}`, '_blank');
    } else if (p === 'SHIPROCKET') {
      window.open('https://www.shiprocket.in/shipment-tracking/', '_blank');
    } else {
      window.open(`https://www.delhivery.com/track/package/${waybill}`, '_blank');
    }
  };

  const getStatusBadge = (status: string) => {
    const displayStatus = normalizeStatus(status) || 'CANCELLED';

    return (
      <span className="px-3 py-1 rounded-lg text-xs font-black bg-red-100 text-red-700 border border-red-200 flex items-center gap-2 w-fit">
        <FaBan /> {displayStatus.replace('_', ' ')}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="bg-gray-50 dark:bg-gray-900 p-6 min-h-screen transition-colors">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl shadow-sm">
            <FaTimesCircle size={24} />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Cancelled Orders</h1>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 bg-red-50/50 dark:bg-red-900/10 p-6 rounded-2xl border border-red-100 dark:border-red-900/30">
          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Search ID / Name / AWB</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Find cancelled order..."
                className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none shadow-sm"
                value={searchIdOrName}
                onChange={(e) => setSearchIdOrName(e.target.value)}
              />
              <FaSearch className="absolute left-3.5 top-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Payment</label>
            <select
              className="w-full p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500 shadow-sm"
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
            >
              <option value="All">All Methods</option>
              <option value="COD">COD</option>
              <option value="PAID">PAID</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Date</label>
            <div className="relative">
              <input
                type="date"
                className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500 shadow-sm"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              />
              <FaCalendarAlt className="absolute left-3.5 top-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-700 shadow-inner bg-white dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100/50 dark:bg-gray-700/50">
              <tr className="text-[11px] font-black text-gray-500 uppercase tracking-tighter">
                <th className="px-6 py-5 text-left">Order ID</th>
                <th className="px-6 py-5 text-left">Customer</th>
                <th className="px-6 py-5 text-left">Cancel Date</th>
                <th className="px-6 py-5 text-left">Tracking (AWB)</th>
                <th className="px-6 py-5 text-left">Amount</th>
                <th className="px-6 py-5 text-left">Status</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-20 text-gray-400 italic">Loading cancelled orders...</td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-20 text-gray-400 font-bold">No cancelled orders found.</td>
                </tr>
              ) : filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-red-50/20 transition-all duration-200">
                  <td className="px-6 py-5 text-xs font-mono font-black text-red-600 dark:text-red-400 tracking-tight">
                    {String(order.id).substring(0, 8)} <br />
                    <span className="text-[10px] text-gray-400 uppercase">{order.source}</span>
                  </td>

                  <td className="px-6 py-5">
                    <div className="text-sm font-black text-gray-900 dark:text-white">
                      {order.user.firstName} {order.user.lastName}
                    </div>
                    <div className="text-[10px] text-gray-400 font-medium">{order.user.mobile}</div>
                  </td>

                  <td className="px-6 py-5 text-xs text-gray-600 dark:text-gray-400 font-bold">
                    <div>{new Date(order.orderDate).toLocaleDateString('en-GB')}</div>
                  </td>

                  <td className="px-6 py-5">
                    {order.delhiveryWaybill ? (
                      <span className="text-xs font-mono font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded inline-block">
                        {order.delhiveryWaybill} <br />
                        <span className="text-[9px] text-gray-500 uppercase">{order.deliveryPartner || 'DELHIVERY'}</span>
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-400 italic">N/A</span>
                    )}
                  </td>

                  <td className="px-6 py-5 text-base font-black text-gray-900 dark:text-white">₹{order.totalAmount}</td>
                  <td className="px-6 py-5">{getStatusBadge(order.status)}</td>

                  <td className="px-6 py-5 text-center">
                    <div className="flex justify-center gap-3">
                      {order.delhiveryWaybill && (
                        <button
                          onClick={() => handleTrack(order.delhiveryWaybill, order.deliveryPartner)}
                          className="p-2.5 text-purple-600 hover:bg-purple-100 rounded-xl transition-all shadow-sm"
                          title="Track Order"
                        >
                          <FaTruck size={18} />
                        </button>
                      )}

                      <button
                        onClick={() => { setSelectedOrder(order); setShowModal(true); }}
                        className="p-2.5 text-blue-600 hover:bg-blue-100 rounded-xl transition-all shadow-sm"
                        title="View Order"
                      >
                        <FaEye size={18} />
                      </button>

                      {!isAgent && (
                        <button
                          onClick={() => handleDelete(order.id, order.source)}
                          className="p-2.5 text-red-600 hover:bg-red-100 rounded-xl transition-all shadow-sm"
                          title="Delete Order"
                        >
                          <FaTrashAlt size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="bg-white rounded-[2rem] max-w-4xl w-full max-h-[92vh] overflow-y-auto border-4 border-gray-50">
            <div className="sticky top-0 bg-white/80 backdrop-blur-sm px-10 py-6 border-b border-gray-100 flex justify-between items-center z-10">
              <div>
                <h2 className="text-3xl font-black text-gray-900 tracking-tighter">
                  Order Details <span className="text-sm bg-gray-200 px-2 rounded">{selectedOrder.source}</span>
                </h2>
                <p className="text-xs text-blue-600 font-mono mt-1 font-bold">ID: {selectedOrder.id}</p>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="p-3 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-all"
              >
                <FaTimes size={28} />
              </button>
            </div>

            <div className="p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                      <FaUser size={16} /> Customer
                    </h3>
                    <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 shadow-inner">
                      <p className="text-xl font-black text-gray-900">
                        {selectedOrder.user.firstName} {selectedOrder.user.lastName}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{selectedOrder.user.email}</p>
                      <p className="text-sm text-gray-400 mt-0.5">{selectedOrder.user.mobile}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-black text-emerald-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                      <FaShippingFast size={16} /> Shipping Point
                    </h3>
                    <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 text-sm text-gray-700 leading-relaxed font-semibold italic">
                      {selectedOrder.source === 'WEBSITE'
                        ? selectedOrder.originalData.shippingAddressSnapshot
                        : `${selectedOrder.originalData.houseNumber || ''}, ${selectedOrder.originalData.streetArea || ''}, ${selectedOrder.originalData.city || ''}, ${selectedOrder.originalData.state || ''} - ${selectedOrder.originalData.pincode || ''}`}
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xs font-black text-orange-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5">
                      <FaCreditCard size={16} /> Status & Payment
                    </h3>
                    <div className="bg-gray-50/50 p-7 rounded-3xl border border-gray-100 shadow-inner space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 font-bold uppercase">Status</span>
                        {getStatusBadge(selectedOrder.status)}
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 font-bold uppercase">Payment</span>
                        <span className="text-sm font-black text-gray-900">{selectedOrder.paymentMode}</span>
                      </div>

                      {selectedOrder.delhiveryWaybill && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-xs text-gray-500 uppercase font-bold mb-1">Tracking AWB</p>
                          <div className="flex justify-between items-center gap-4">
                            <div>
                              <span className="text-lg font-mono font-bold text-purple-700 block">
                                {selectedOrder.delhiveryWaybill}
                              </span>
                              <span className="text-[10px] text-gray-400 font-black uppercase">
                                {selectedOrder.deliveryPartner || 'DELHIVERY'}
                              </span>
                            </div>

                            <button
                              onClick={() => handleTrack(selectedOrder.delhiveryWaybill, selectedOrder.deliveryPartner)}
                              className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded shadow-sm"
                            >
                              Track Now
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-end border-t border-gray-200 pt-4 mt-4">
                        <span className="text-lg font-black text-gray-600">Total</span>
                        <span className="text-4xl font-black text-blue-700">₹{selectedOrder.totalAmount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-10 border-t-2 border-gray-50">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.4em] mb-8 flex items-center gap-3">
                  <FaBoxOpen size={18} /> Ordered Items
                </h3>

                <div className="grid grid-cols-1 gap-5">
                  {selectedOrder.source === 'WEBSITE' ? (
                    selectedOrder.originalData.orderItems?.map((item: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-6 bg-white p-5 border-2 border-gray-50 rounded-[1.5rem] hover:border-blue-300 transition-all">
                        <div className="w-20 h-20 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0">
                          <img src={item.imageUrl || '/Logo/default-product.png'} className="w-full h-full object-cover" alt="" />
                        </div>
                        <div className="flex-1">
                          <p className="text-lg font-black text-gray-900">{item.productName}</p>
                          <p className="text-sm text-gray-400 font-bold mt-1">Qty: {item.quantity} | ₹{item.price}</p>
                        </div>
                        <div className="text-2xl font-black text-gray-900 pr-4">₹{item.quantity * item.price}</div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-6 bg-white p-5 border-2 border-gray-50 rounded-[1.5rem] hover:border-blue-300 transition-all">
                      <div className="w-20 h-20 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                        <FaBoxOpen size={32} className="text-gray-300" />
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-black text-gray-900">{selectedOrder.originalData.medicine || 'Medicine'}</p>
                        <p className="text-sm text-gray-400 font-bold mt-1">
                          Qty: {selectedOrder.originalData.medicineQuantity || 1} | ₹{selectedOrder.originalData.medicinePrice || selectedOrder.totalAmount}
                        </p>
                      </div>
                      <div className="text-2xl font-black text-gray-900 pr-4">
                        ₹{selectedOrder.originalData.payableAmount || selectedOrder.totalAmount}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-10 py-8 bg-gray-50/80 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-12 py-4 bg-gray-900 text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] hover:bg-black transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CancelledOrderList;
